import { defineStore } from 'pinia'
import { JourneySearchResult } from '../types/JourneyTypes'
import { Passenger, PassengerType } from '../types/passengerTypes'
import { Station } from '../types/stationTypes'
import { fetchDestinationSuggestions } from '../services/journeyService'
import { Booking, Ticket } from '../types/bookingTypes'
import { SelectedSeat } from '../types/seatTypes'
import { useMyTravelStore } from './myTravelStore'

export const useJourneyStore = defineStore('journey', {
  state: () => ({
    stationName: '' as string,
    uicStationCode: '' as string,
    OriginStation: {
      name: '',
      _u_i_c_station_code: '',
    } as Station,
    destinationStation: {
      name: '',
      _u_i_c_station_code: '',
    } as Station,
    destinationSuggestions: [] as string[],
    departureDate: (() => {
      const now = new Date()
      const april1st = new Date('2025-04-01T00:00:00+01:00')

      if (now >= april1st) {
        // After April 1st, use current date
        return now.toISOString().split('T')[0]
      }
      // Before April 1st, use April 1st
      return '2025-04-01'
    })(),
    returnDate: '' as string,
    showOutbound: true,
    passengerType: null as PassengerType | null,
    journeySearchResults: null as JourneySearchResult | null,
    allStations: [] as Station[],
    bookingResponse: null as string | null,
    bookingData: null as Booking | null,
    passengers: [] as Passenger[],
    selectedSeats: [] as SelectedSeat[],
    selectedProducts: [] as Ticket[],
    selectedBundle: null as any | null,
    isRebooking: false,
    rebookingType: '' as 'full' | 'outbound' | 'inbound' | '',
    originalBookingNumber: '' as string,
    rebookPayload: null as any,
  }),
  actions: {
    async setOriginStation(station: Station) {
      this.OriginStation = station
      this.destinationSuggestions = [] // Reset to empty array before fetching
      try {
        const suggestions = await fetchDestinationSuggestions(station._u_i_c_station_code)
        this.destinationSuggestions = suggestions
      } catch (error) {
        console.error('Error fetching destination suggestions:', error)
      }
    },
    setDestinationStation(station: Station) {
      this.destinationStation = station
    },
    setDepartureDate(date: string) {
      this.departureDate = date
    },
    setReturnDate(date: string | null) {
      this.returnDate = date ?? ''
    },
    //deprecated
    setPassengerType(type: PassengerType) {
      this.passengerType = type
    },
    setPassengers(passengers: Passenger[]) {
      this.passengers = passengers
    },
    setJourneySearchResults(results: JourneySearchResult) {
      this.journeySearchResults = results
      // Clear selected products when new search results come in
      this.selectedProducts = []
      // Clear selected seats as well since they're related
      this.selectedSeats = []
      // Clear selected bundle to ensure no radio buttons remain selected
      this.selectedBundle = null

      const myTravelStore = useMyTravelStore()
      myTravelStore.reset()
    },
    setAllStations(stations: Station[]) {
      this.allStations = stations
    },
    setBookingResponse(response: string) {
      this.bookingResponse = response
    },
    setBookingData(data: Booking) {
      this.bookingData = data
    },
    setSelectedBundle(bundle: any) {
      this.selectedBundle = bundle
    },
    addSelectedSeat(seat: SelectedSeat) {
      this.selectedSeats = this.selectedSeats.filter((s) => s.seat_number !== seat.seat_number)
      this.selectedSeats.push(seat)
    },
    removeSelectedSeat(seat_number: string) {
      this.selectedSeats = this.selectedSeats.filter((seat) => seat.seat_number !== seat_number)
    },
    removeSelectedProduct(tariffCode: string) {
      this.selectedProducts = this.selectedProducts.filter(
        (product) => product.product_family !== tariffCode,
      )
    },
    addTicket(ticket: any) {
      // Filter out any existing tickets for this passenger and travel direction
      const currentProducts = [...this.selectedProducts]

      // Filter out any existing tickets for this passenger and travel direction
      const filteredProducts = currentProducts.filter(
        (p) => !(p.passenger_id === ticket.passenger_id && p.travelIndex === ticket.travelIndex),
      )

      // Add the new ticket
      this.selectedProducts = [...filteredProducts, ticket]
    },
    resetStore() {
      this.$reset()
    },
    initializeRebooking(type: 'full' | 'outbound' | 'inbound', bookingNumber: string) {
      this.isRebooking = true
      this.rebookingType = type
      this.originalBookingNumber = bookingNumber
    },

    setFromStation(stationId: string) {
      const station = this.allStations.find((s) => s._u_i_c_station_code === stationId)
      if (station) {
        this.setOriginStation(station)
      }
    },

    setToStation(stationId: string) {
      const station = this.allStations.find((s) => s._u_i_c_station_code === stationId)
      if (station) {
        this.setDestinationStation(station)
      }
    },

    prefillRebookingData(booking: Booking) {
      if (this.rebookingType === 'outbound' || this.rebookingType === 'full') {
        const outboundSegment = booking.outbound_booking_tariff_segments?.[0]
        if (outboundSegment) {
          this.setFromStation(outboundSegment.departure_station._u_i_c_station_code)
          this.setToStation(outboundSegment.arrival_station._u_i_c_station_code)

          if (this.rebookingType === 'outbound') {
            this.setDepartureDate(outboundSegment.travel_date)
          }
        }
      }

      if (this.rebookingType === 'inbound' || this.rebookingType === 'full') {
        const inboundSegment = booking.inbound_booking_tariff_segments?.[0]
        if (inboundSegment) {
          if (this.rebookingType === 'full') {
            this.setReturnDate(inboundSegment.travel_date)
          } else {
            this.setFromStation(inboundSegment.departure_station._u_i_c_station_code)
            this.setToStation(inboundSegment.arrival_station._u_i_c_station_code)
            this.setDepartureDate(inboundSegment.travel_date)
          }
        }
      }

      // Copy passenger data with required fields
      this.passengers = booking.passengers.map((passenger) => ({
        id: passenger.id,
        type: passenger.type,
        first_name: passenger.first_name || '',
        last_name: passenger.last_name || '',
        email: passenger.email || '',
        phone_number: passenger.phone_number || '',
        discount_cards: passenger.discount_cards || [],
        protect_privacy: passenger.protect_privacy || false,
      }))

      // Clear any previously selected seats or products
      this.selectedSeats = []
      this.selectedProducts = []
    },

    prepareRebookingQuery(booking: Booking, type: 'full' | 'outbound' | 'inbound') {
      if (!booking.booking_number) {
        throw new Error('Booking number is required')
      }

      let query: Record<string, string> = {
        isRebooking: 'true',
        bookingNumber: booking.booking_number,
        type,
      }

      if (type === 'full' || type === 'outbound') {
        const outboundSegment = booking.outbound_booking_tariff_segments?.[0]
        if (!outboundSegment) {
          throw new Error('No outbound segment found')
        }
        query = {
          ...query,
          from: outboundSegment.departure_station._u_i_c_station_code,
          to: outboundSegment.arrival_station._u_i_c_station_code,
        }
      } else {
        const inboundSegment = booking.inbound_booking_tariff_segments?.[0]
        if (!inboundSegment) {
          throw new Error('No inbound segment found')
        }
        query = {
          ...query,
          from: inboundSegment.departure_station._u_i_c_station_code,
          to: inboundSegment.arrival_station._u_i_c_station_code,
        }
      }

      return query
    },
    setShowOutbound(value: boolean) {
      this.showOutbound = value
    },
    clearDiscountCards() {
      // Map over existing passengers and remove their discount cards while keeping other info
      this.passengers = this.passengers.map((passenger) => ({
        ...passenger,
        discount_cards: [],
      }))
    },
    setRebookPayload(payload: any) {
      this.rebookPayload = payload
    },
  },
  getters: {
    // Map UIC codes to station names for display
    mappedDestinationSuggestions: (state) => {
      // Ensure we always have an array to map over
      const suggestions = state.destinationSuggestions || []
      return suggestions.map((uicCode) => {
        const matchedStation = state.allStations.find(
          (station: Station) => station._u_i_c_station_code === uicCode,
        )
        return {
          uicCode,
          name: matchedStation ? matchedStation.name : uicCode,
          sequence_number: matchedStation ? matchedStation.sequence_number : 100,
          district: matchedStation?.district,
          county: matchedStation?.county,
        }
      })
    },
  },
})
